import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowDown, ArrowUp, Loader2, Paperclip, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { safeContentType, sanitizeFilename } from '@/lib/sanitizeFilename';
import {
  CONTENT_ATTACHMENT_ACCEPT,
  CONTENT_ATTACHMENTS_BUCKET,
  formatBytes,
  validateContentAttachment,
} from '@/lib/contentAttachments';

interface Props {
  contentId: string | null;
}

export function ContentAttachmentsEditor({ contentId }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const key = ['content-attachments', contentId];

  const { data: items = [] } = useQuery({
    queryKey: key,
    enabled: !!contentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_bank_attachments')
        .select('*')
        .eq('content_id', contentId!)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ['content-attachment-counts'] });
  };

  if (!contentId) {
    return (
      <div className="grid gap-2">
        <Label>Adjuntos (opcional)</Label>
        <p className="text-xs text-muted-foreground">Guarda el contenido primero para poder agregar adjuntos.</p>
      </div>
    );
  }

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const list = Array.from(files);
    for (const file of list) {
      const v = validateContentAttachment(file);
      if (!v.ok) { toast.error(`${file.name}: ${v.error}`); return; }
    }
    setUploading(true);
    try {
      let order = items.length;
      for (const file of list) {
        const path = `${contentId}/${Date.now()}-${sanitizeFilename(file.name)}`;
        const { error: upErr } = await supabase.storage
          .from(CONTENT_ATTACHMENTS_BUCKET)
          .upload(path, file, { contentType: safeContentType(file) });
        if (upErr) throw upErr;
        const { error } = await supabase.from('content_bank_attachments').insert({
          content_id: contentId,
          file_name: file.name,
          display_name: file.name.replace(/\.[^.]+$/, ''),
          storage_path: path,
          mime_type: file.type || null,
          size_bytes: file.size,
          sort_order: order++,
        });
        if (error) throw error;
      }
      toast.success('Adjuntos agregados');
      refresh();
    } catch (e: any) {
      toast.error(e?.message ?? 'No se pudo subir el adjunto');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const rename = async (id: string, display_name: string) => {
    if (!display_name.trim()) return;
    const { error } = await supabase.from('content_bank_attachments').update({ display_name: display_name.trim() }).eq('id', id);
    if (error) toast.error(error.message); else refresh();
  };

  const remove = async (id: string, path: string) => {
    if (!confirm('¿Eliminar este adjunto?')) return;
    await supabase.storage.from(CONTENT_ATTACHMENTS_BUCKET).remove([path]);
    const { error } = await supabase.from('content_bank_attachments').delete().eq('id', id);
    if (error) toast.error(error.message); else refresh();
  };

  const move = async (index: number, dir: -1 | 1) => {
    const a = items[index], b = items[index + dir];
    if (!a || !b) return;
    await Promise.all([
      supabase.from('content_bank_attachments').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('content_bank_attachments').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
    refresh();
  };

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <Label>Adjuntos (opcional)</Label>
        <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()}>
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          Subir archivos
        </Button>
        <input ref={inputRef} type="file" multiple hidden accept={CONTENT_ATTACHMENT_ACCEPT} onChange={(e) => handleFiles(e.target.files)} />
      </div>
      <p className="text-xs text-muted-foreground">Datasets, plantillas o guías (PDF, Office, CSV, TXT, MD, JSON, ZIP, PNG/JPG; máx. 50 MB).</p>
      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={it.id} className="flex items-center gap-2 rounded-md border border-border p-2">
              <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
              <Input
                defaultValue={it.display_name}
                className="h-8"
                onBlur={(e) => e.target.value !== it.display_name && rename(it.id, e.target.value)}
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">{formatBytes(it.size_bytes)}</span>
              <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Subir">
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Bajar">
                <ArrowDown className="w-4 h-4" />
              </Button>
              <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(it.id, it.storage_path)} aria-label="Eliminar adjunto">
                <Trash2 className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
